import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './glassPatternsDefs';

export function drawMixedShape(
  svg,
  initX,
  initY,                 // Top of the arch
  w,
  hRect,                 // Rectangle height (can be 0)
  archHeight,            // Height of arch (vertical radius)
  frameWidth = 10,
  frameColor = 'white',
  lineFrameColor = 'black',
  outerStrokeColor = 'black',
  outerStrokeWidth = 1,
  color = '#EDF9FF',     // Default glass color
  includeInnerTopLine = false,
  glassType = null,      // 'Tinted' | 'Textured' | null
  glassDesign = null     // e.g. 'Grey', 'Rain blast', etc.
) {
  const rX = w / 2;
  const rY = archHeight;

  const left = initX;
  const right = initX + w;
  const centerX = initX + rX;
  const centerY = initY + rY;
  const bottom = initY + archHeight + hRect;

  const fw = frameWidth;

  // ====== Glass Fill (Base Layer) ======
  let baseFill = color;
  let usePattern = false;
  let tintOverlay = null;

  // TINTED GLASS
  if (glassType === 'Tinted') {
    baseFill = 'white'; // base is solid white, then tint overlays
    switch (glassDesign?.toLowerCase()) {
      case 'grey':
        tintOverlay = 'rgba(60, 60, 60, 0.2)';
        break;
      case 'bronze':
        tintOverlay = 'rgba(90, 50, 0, 0.2)';
        break;
      default:
        console.warn('Unknown tinted glass design:', glassDesign);
    }
  }

  // TEXTURED GLASS
  if (glassType === 'Textured' && glassDesign) {
    const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
    baseFill = `url(#${patternId})`;
    usePattern = true;
    defineTexturedGlassPatterns(svg, glassDesign);
    //defineTexturedGlassPatterns(svg, 'Rain blast');
  }

  // === Outer path (frame) ===
  const pathOuter = `
    M ${left - fw},${bottom + fw}
    L ${left - fw},${centerY}
    A ${rX + fw},${rY + fw} 0 0 1 ${right + fw},${centerY}
    L ${right + fw},${bottom + fw}
    Z
  `;

  svg.append('path')
    .attr('d', pathOuter)
    .attr('fill', frameColor)
    .attr('stroke', lineFrameColor)
    .attr('stroke-width', 1);

  // === Inner path (shape fill) ===
  let pathInner;
  if (hRect > 0) {
    // Arch + rectangle
    pathInner = `
      M ${left},${bottom}
      L ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${bottom}
      Z
    `;
  } else {
    // Arch only
    pathInner = `
      M ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${centerY}
      L ${left},${centerY}
      Z
    `;
  }

  // === Base glass fill (pattern or solid white or default color) ===
  svg.append('path')
    .attr('d', pathInner)
    .attr('fill', baseFill)
    .attr('stroke', 'none');

  // === Tint overlay (semi-transparent) ===
  if (tintOverlay) {
    svg.append('path')
      .attr('d', pathInner)
      .attr('fill', tintOverlay)
      .attr('stroke', 'none');
  }

  // === Top stroke outline ===
  svg.append('path')
    .attr('d', pathInner)
    .attr('fill', 'none')
    .attr('stroke', outerStrokeColor)
    .attr('stroke-width', outerStrokeWidth);

  // === Optional inner rectangular top line ===
  if (includeInnerTopLine && hRect > 0) {
    svg.append('rect')
      .attr('x', left - frameWidth)
      .attr('y', centerY)
      .attr('width', w + 2 * frameWidth)
      .attr('height', frameWidth)
      .attr('fill', frameColor)
      .attr('stroke', lineFrameColor)
      .attr('stroke-width', 1);
  }
}




export function drawArch(
    svg,
    d,
    x,
    y,
    w,
    h,
    labelText = null,
    subFrame = false,
    frameWidth = 10,
    frameColor = 'white',
    lineFrameColor = 'black',
    outerStrokeColor = 'black',
    outerStrokeWidth = 1,
    color = '#EDF9FF',
    labelOptions = {}
  ) {
    const {
      fontSize = 14,
      fontColor = 'black',
      fontFamily = 'Arial',
      fontOpacity = 0.4
    } = labelOptions;
  
    const innerX = x + frameWidth;
    const innerY = y + frameWidth;
    const innerW = w - 2 * frameWidth;
    const innerH = h - frameWidth;
  
    // Fill main inner shape
    svg.append('rect')
      .attr('x', innerX)
      .attr('y', innerY)
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', color)
      .attr('stroke', 'none');
  
    // === Draw arch frame ===
    const archPath = `
      M ${x} ${y + h}
      A ${w / 2} ${h} 0 0 1 ${x + w} ${y + h}
    `;
  
    // Arch frame fill (thick stroke)
    svg.append('path')
      .attr('d', archPath)
      .attr('fill', 'none')
      .attr('stroke', frameColor)
      .attr('stroke-width', frameWidth);
  
    // Arch outline
    svg.append('path')
      .attr('d', archPath)
      .attr('fill', 'none')
      .attr('stroke', lineFrameColor)
      .attr('stroke-width', 1);
  
    // === Bottom frame ===
    svg.append('line')
      .attr('x1', x)
      .attr('y1', y + h)
      .attr('x2', x + w)
      .attr('y2', y + h)
      .attr('stroke', frameColor)
      .attr('stroke-width', frameWidth);
  
    // Bottom frame outline
    svg.append('line')
      .attr('x1', x)
      .attr('y1', y + h)
      .attr('x2', x + w)
      .attr('y2', y + h)
      .attr('stroke', lineFrameColor)
      .attr('stroke-width', 1);
  
    // === Optional outer stroke (on top of all) ===
    if (outerStrokeWidth > 0) {
      svg.append('path')
        .attr('d', archPath)
        .attr('fill', 'none')
        .attr('stroke', outerStrokeColor)
        .attr('stroke-width', outerStrokeWidth);
  
      svg.append('line')
        .attr('x1', x)
        .attr('y1', y + h)
        .attr('x2', x + w)
        .attr('y2', y + h)
        .attr('stroke', outerStrokeColor)
        .attr('stroke-width', outerStrokeWidth);
    }
  
    // === Label ===
    if (labelText !== null) {
      svg.append('text')
        .attr('x', x + w / 2)
        .attr('y', y + h / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', fontColor)
        .attr('fill-opacity', fontOpacity)
        .attr('font-family', fontFamily)
        .text(labelText);
    }
  }
  

  
  